import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

const DURATION_MS = 200;

// Ref-counted body scroll lock so the page only unlocks when the last open
// drawer closes (correct even if two ever mount at once).
let lockCount = 0;
let savedOverflow = "";
function lockScroll() {
  if (lockCount === 0) {
    savedOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
  }
  lockCount += 1;
}
function unlockScroll() {
  lockCount = Math.max(0, lockCount - 1);
  if (lockCount === 0) document.body.style.overflow = savedOverflow;
}

/**
 * Right-side drawer. Slides in from the right, full height, scrollable body —
 * works on desktop and mobile (full width on small screens). Same
 * `{ title, onClose, children }` API as before; `wide` widens it for forms.
 */
export function Modal({
  title,
  onClose,
  children,
  wide = false,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  wide?: boolean;
}) {
  const [shown, setShown] = useState(false);
  const rafs = useRef<number[]>([]);

  // Slide in after mount (double rAF so the closed frame paints first); lock
  // background scroll while open.
  useEffect(() => {
    lockScroll();
    const r1 = requestAnimationFrame(() => {
      const r2 = requestAnimationFrame(() => setShown(true));
      rafs.current.push(r2);
    });
    rafs.current.push(r1);
    const pending = rafs.current;
    return () => {
      for (const r of pending) cancelAnimationFrame(r);
      unlockScroll();
    };
  }, []);

  // Slide out, then let the parent unmount us.
  const close = useCallback(() => {
    setShown(false);
    window.setTimeout(onClose, DURATION_MS);
  }, [onClose]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [close]);

  return (
    <div className="fixed inset-0 z-40">
      <div
        className={`absolute inset-0 bg-slate-900/40 transition-opacity duration-200 ${
          shown ? "opacity-100" : "opacity-0"
        }`}
        onClick={close}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`absolute inset-y-0 right-0 flex w-full flex-col bg-white shadow-xl transition-transform duration-200 ease-out ${
          wide ? "sm:max-w-2xl" : "sm:max-w-md"
        } ${shown ? "translate-x-0" : "translate-x-full"}`}
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <h3 className="text-base font-semibold">{title}</h3>
          <button
            onClick={close}
            aria-label="Schliessen"
            className="text-sm text-slate-400 hover:text-slate-700"
          >
            ✕
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">{children}</div>
      </div>
    </div>
  );
}
